import React, { PureComponent } from 'react';
import ReactGA from 'react-ga';
import TagManager from 'react-gtm-module';
import {
  BASE_SCENE_ID,
  LOGIN_EVENT,
  LOGOUT_EVENT,
  MOVEMENT_SEARCH_RANGE,
  IN_TO_M,
} from '../constants';
import appApiCreater from '../modules';
import { fetchSpreadsheetData } from '../scripts/buildCompatData';
import initScene, { updateColor } from '../helper/scene';
import { getDealInfo } from '../helper/dealer';
import { getActiveCommand } from '../helper/player';
import { getNodeInfo } from '../helper/config';
import message from '../helper/message';

const AppContext = React.createContext('app');

export default class AppProvider extends PureComponent {
  constructor(props) {
    super(props);
    const { dealer } = props;
    this.modelId = undefined;
    this.activeTool = 'orbit';
    this.mousedown = false;
    this.state = { disabled: true, dealer };
  }

  componentWillUpdate(nextProps, nextState) {
    const { appApi, selectedModel } = nextState;
    selectedModel
      ? appApi.model.selectModel(selectedModel)
      : appApi.model.deselectModel();
  }

  async componentDidMount() {
    const { orderId, orderHash, token, org } = this.props;
    const claraApi = window.claraplayer('player');
    await claraApi.sceneIO.fetchAndUse(BASE_SCENE_ID, null, {
      waitForPublish: true,
    });
    //Add page tracking
    ReactGA.initialize('UA-442059-2');
    ReactGA.pageview(window.location.pathname + window.location.search);

    TagManager.initialize({ gtmId: 'GTM-TJS7SJW' });

    let metadata;
    if (process.env.REACT_APP_ENV === 'PROD') {
      await claraApi.configuration.initConfiguration({ id: BASE_SCENE_ID });
      metadata = claraApi.configuration.getAttribute('Metadata').metadata;
    } else {
      metadata = await fetchSpreadsheetData();
      window.claraApi = claraApi;
    }

    claraApi.player.removeTool('select');
    const highlighter = claraApi.selection.setHighlighting(true);
    const appApi = appApiCreater(claraApi, metadata, highlighter);

    let orderData;
    if (orderHash || orderId) {
      try {
        orderData = await (orderHash
          ? appApi.order.fetchOrderFromHash(orderHash)
          : appApi.order.fetchOrder(orderId, token, org));
      } catch (e) {
        message.warn(
          `Fail to fetch order with ${
            orderHash ? `hash ${orderHash}` : `orderId ${orderId}`
          }.`
        );
      }
    }

    claraApi.player.addTool({
      name: 'ModelSelector',
      click: (ev) => {
        if (orderId) return;
        const { selectedModel, models } = this.state;
        if (!this.modelId)
          return selectedModel && this.setState({ selectedModel: null });

        this.setState({
          selectedModel: this.modelId,
          activeTab: models[this.modelId].tab,
        });
      },
      mousedown: (ev) => {
        this.activeTool = getActiveCommand(claraApi);
        this.mousedown = true;
        if (orderId || ev.which !== 1) return;
        const { models, appApi, configuration, selectedModel } = this.state;
        const nodeId = claraApi.player.filterNodesFromPosition(ev)[0];
        this.modelId = appApi.model.findModel(nodeId);
        if (!this.modelId) return;
        const mouseDownModelId = this.modelId;
        const { attribute, value, parent } = models[mouseDownModelId];
        if (!parent) return;

        const overlayCheck = appApi.config.getSpecialOverlayValidationData(
          models[mouseDownModelId].value,
          configuration,
          models
        );
        const {
          dependOnBlock: block,
          secondaryDependOnBlocks: secondaryBlocks,
        } = appApi.config.getModelDependOnBlocks(
          mouseDownModelId,
          configuration,
          models
        );

        // if the block has a single entry, this block is used more like attachment reference
        if (!block || block.isSingleEntryBlock()) return;

        appApi.model.selectModel(mouseDownModelId);
        const nodeInfo = getNodeInfo(appApi.config.getProduct(value));

        const movementOptions = {
          onStart: () => {
            // onStart callback
            if (!secondaryBlocks) return;
            secondaryBlocks.forEach((secondaryBlock) =>
              secondaryBlock.unoccupyBlocks(mouseDownModelId)
            );
          },
          onMove: (insertion) => {
            // onMove callback
            let canMove = true;
            if (!secondaryBlocks) return canMove;
            for (let idx = 0; canMove && idx < secondaryBlocks.length; ++idx) {
              secondaryBlocks[idx].forceInsert(
                nodeInfo,
                insertion.rowStart,
                insertion.colStart,
                (models) =>
                  models.forEach((model) => (canMove = canMove && model.valid))
              );
            }
            return canMove;
          },
          onEnd: (insertion) => {
            // onEnd callback
            selectedModel
              ? appApi.model.selectModel(selectedModel)
              : appApi.model.deselectModel();
            let selfBlocks = configuration[attribute].blocks;
            if (!selfBlocks) {
              const selfConfigState =
                configuration[attribute][value] &&
                configuration[attribute][value].find(
                  ({ modelId }) => modelId === mouseDownModelId
                );
              if (selfConfigState) selfBlocks = selfConfigState.blocks;
              else {
                message.error(
                  `Fail to get the configuration state for ${attribute}, ${value}!`
                );
              }
            }
            if (selfBlocks)
              appApi.model.updateBlocks(mouseDownModelId, selfBlocks);
            if (!secondaryBlocks) return;
            secondaryBlocks.forEach((secondaryBlock) => {
              const forceInsertion = secondaryBlock.forceInsert(
                nodeInfo,
                insertion.rowStart,
                insertion.colStart
              );
              secondaryBlock.occupyBlocks(mouseDownModelId, forceInsertion);
            });
          },
          searchRange: MOVEMENT_SEARCH_RANGE * IN_TO_M,
          overlayCheck,
        };

        claraApi.commands.activateCommand('nodeMove');
        claraApi.commands.setCommandOptions(
          'nodeMove',
          block.getCommandOptions(mouseDownModelId, nodeInfo, movementOptions)
        );
      },
      mouseup: (ev) => {
        if (orderId) return;
        this.mousedown && claraApi.commands.activateCommand(this.activeTool);
        this.mousedown = false;
      },
    });

    let configuration, models;
    if (orderData) {
      const resumeState = await appApi.order.resumeStateFromOrder(
        orderData,
        appApi.model.importModel
      );
      configuration = resumeState.configuration;
      models = resumeState.models;
    } else {
      configuration = appApi.config.initConfiguration();
      models = {};
    }
    configuration = initScene(claraApi, appApi, { configuration, models });
    const formState = appApi.form.initFormState();
    // Note: the actions in this app is a little different than usual actions
    // it will return the newState Object, and you have to explicity apply setAppState to it
    // the reason for that is sometimes multiply actions will run in the same scope
    // and this will avoid the unnecessary rendering

    this.setState({
      claraApi,
      appApi,
      activeTab: 'start',
      disabled: false,
      configuration,
      formState,
      selectedModel: null,
      models,
      actions: {
        setConfiguration: this.setConfiguration,
        addModel: this.addModel,
        removeModel: this.removeModel,
      },
      setAppState: this.setAppState,
      getAppState: this.getAppState,
      orderData,
    });
  }

  onDealerLogin = () => this.setState({ dealer: getDealInfo() });

  onDealerLogout = () => this.setState({ dealer: undefined });

  componentWillMount() {
    window.addEventListener(LOGIN_EVENT, this.onDealerLogin);
    window.addEventListener(LOGOUT_EVENT, this.onDealerLogout);
  }

  componentWillUnmount() {
    window.removeEventListener(LOGIN_EVENT, this.onDealerLogin);
    window.removeEventListener(LOGOUT_EVENT, this.onDealerLogout);
  }

  setAppState = (newState) =>
    this.setState(
      newState,
      process.env.REACT_APP_ENV !== 'PROD'
        ? () => console.log(this.getAppState())
        : undefined
    );

  getAppState = () => {
    const {
      activeTab,
      configuration,
      selectedModel,
      models,
      disabled,
    } = this.state;
    return {
      activeTab,
      configuration,
      selectedModel,
      models,
      disabled,
    };
  };

  setConfiguration = (changes, optionState) => {
    const { appApi, models } = this.state;
    const newState = optionState || this.getAppState();

    return Object.assign(newState, {
      configuration: appApi.config.setConfiguration(
        newState.configuration,
        models,
        Array.isArray(changes) ? changes : [changes]
      ),
    });
  };

  addModel = (modelState, value, optionState, autoSelect = true) => {
    const { claraApi, configuration } = this.state;
    const newState = optionState || this.getAppState();
    const newModels = newState.models;
    const { modelId, sceneId } = modelState;
    const paintColor = configuration.colorStandardPaint.value;
    const laminateColor = configuration.colorStandardLaminate.value;
    updateColor(claraApi, 'Metal', paintColor, modelId);
    updateColor(claraApi, 'Laminate', laminateColor, modelId);
    if (value.parent) {
      Object.assign(newModels, {
        [value.parent]: {
          ...newModels[value.parent],
          children: newModels[value.parent].children.concat([modelId]),
        },
      });
    }
    Object.assign(newModels, {
      [modelId]: { ...value, sceneId, children: [] },
    });
    if (autoSelect) Object.assign(newState, { selectedModel: modelId });

    return Object.assign(newState, { models: newModels });
  };

  removeModel = (modelId, optionState) => {
    const { appApi } = this.state;
    const newState = optionState || this.getAppState();
    const newModels = appApi.model.deleteModel(newState.models, modelId);
    if (!newModels[newState.selectedModel]) newState.selectedModel = null;
    return Object.assign(newState, { models: newModels });
  };

  render() {
    return (
      <AppContext.Provider value={{ ...this.state }}>
        {this.props.children}
      </AppContext.Provider>
    );
  }
}

export function withApi(PureComponent) {
  return (props) => (
    <AppContext.Consumer>
      {(apiProps) => <PureComponent {...props} {...apiProps} />}
    </AppContext.Consumer>
  );
}



// WEBPACK FOOTER //
// ./src/context/AppContext.js